import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-024142ee"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "p-recommend-form-type-wrap" }
const _hoisted_2 = ["name"]
const _hoisted_3 = {
  key: 0,
  class: "p-recommend-form__select-list__item p-recommend-form__select-list__item--loading"
}
const _hoisted_4 = ["onMousedown"]
const _hoisted_5 = {
  key: 0,
  class: "p-recommend-form__select-list__item p-recommend-form__select-list__item--no-match"
}

import { ref, onMounted, watch, computed } from 'vue';
import { MstMyPageItem } from '@/types/mstMyPage';
import { RuleExpression, useField } from 'vee-validate';
import ValidatableField from '@/components/ValidatableField.vue';
import {
  getAllLicenseByNameService,
  getLicenseNameByIdService,
  getAllschlByNameService,
  getSchlNameByIdService,
  getAllStationByNameService,
  getStationNameByIdService,
} from '@/api/services/mstMyPageService';

type SelectOption = {
  id: string | number;
  text: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ResumeSearch',
  props: {
    name: null,
    id: null,
    label: { default: '' },
    rules: null,
    modelValue: { default: '' },
    resetKey: null,
    afterSelectChange: null
  },
  emits: ['update:modelValue'],
  setup(__props: any, { emit }) {

const props = __props as { name: string, id: string, label: string, rules?: RuleExpression<unknown>, modelValue: string | number, resetKey?: number, afterSelectChange?: (selected: SelectOption) => void }





const options = ref<Array<SelectOption>>([]);
const searchText = ref('');
const selectedValue = ref('');
const showDropdown = ref(false);
const isSearching = ref(false);
const noResults = ref(false);
let searchTimeout: number | null = null;

// Use useField to connect with vee-validate
const valueField = useField(props.name, props.rules, {
  label: props.label,
  initialValue: props.modelValue
});

// Filtered options now comes from API search results
const filteredOptions = computed(() => {
  return options.value;
});

watch(() => props.resetKey, (newVal) => {
  if (newVal) {
    valueField.value.value = '';
    searchText.value = '';
  }
});

// Watch for changes in modelValue to update value
watch(() => props.modelValue, (newVal) => {
  if (newVal !== undefined && newVal !== valueField.value.value) {
    valueField.value.value = newVal;
    // Find the option text for the selected ID and update searchText
    updateSearchTextFromValue(newVal);
  }
});

// Watch for changes in value to emit update event
watch(valueField.value, (newVal) => {
  if (!valueField.meta.touched && newVal) {
    fetchOptionById(newVal as string);
  }
  emit('update:modelValue', newVal);
});

// Handle search input with debounce
function handleSearch() {
  selectedValue.value = '';
  // Clear any existing timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (searchText.value.length >= 2) {
    showDropdown.value = true;
    isSearching.value = true;
    noResults.value = false;
    
    // Set a new timeout
    searchTimeout = window.setTimeout(async () => {
      switch (props.id) {
        case 'licenses': {
          await searchLicensesByName(searchText.value);
          break;
        }        
        case 'stations': {
          await searchStationByName(searchText.value);
          break;
        }
        case 'schl': {
          await searchSchlByName(searchText.value);
          break;
        }
      }
      searchTimeout = null;
    }, 1000); // 1 second debounce
  } else {
    showDropdown.value = false;
    isSearching.value = false;
    noResults.value = false;
    options.value = [];
  }
}

// Search licenses by name
async function searchLicensesByName(name: string) {
  try {
    if (name.length < 2) return;

    const data = await getAllLicenseByNameService(name);
    options.value = await mapToSelectOption(data);
    
    // Update UI state
    isSearching.value = false;
    noResults.value = options.value.length === 0;
    
    // If no matches are found, clear the value
    if (options.value.length === 0) {
      valueField.value.value = '';
    }
  } catch (error) {
    console.error('Error searching licenses:', error);
    isSearching.value = false;
    noResults.value = true;
    options.value = [];
  }
}

// Search station by name
async function searchStationByName(name: string) {
  try {
    if (name.length < 2) return;

    const data = await getAllStationByNameService(name);
    options.value = await mapToSelectOption(data);
    
    // Update UI state
    isSearching.value = false;
    noResults.value = options.value.length === 0;
    
    // If no matches are found, clear the value
    if (options.value.length === 0) {
      valueField.value.value = '';
    }
  } catch (error) {
    console.error('Error searching station:', error);
    isSearching.value = false;
    noResults.value = true;
    options.value = [];
  }
}

// Search school by name
async function searchSchlByName(name: string) {
  try {
    if (name.length < 2) return;

    const data = await getAllschlByNameService(name);
    options.value = await mapToSelectOption(data);
    
    // Update UI state
    isSearching.value = false;
    noResults.value = options.value.length === 0;
    
    // If no matches are found, clear the value
    if (options.value.length === 0) {
      valueField.value.value = '';
    }
  } catch (error) {
    console.error('Error searching station:', error);
    isSearching.value = false;
    noResults.value = true;
    options.value = [];
  }
}

// Handle blur event with delay to allow click on dropdown items
function handleBlur() {
  if (!selectedValue.value) {
    searchText.value = '';
  }
  if (!searchText.value) {
    valueField.value.value = '';
  }
  
  // If the search text doesn't match any option, clear it when losing focus
  if (searchText.value && valueField.value.value === '') {
    const matchingOption = options.value.find(option => 
      option.text.toLowerCase() === searchText.value.toLowerCase()
    );
    
    if (!matchingOption) {
      searchText.value = '';
    }
  }
  
  options.value = [];
  setTimeout(() => {
    showDropdown.value = false;
    isSearching.value = false;
  }, 200);
}

// Select an option from the dropdown
function selectOption(option: SelectOption) {
  valueField.value.value = option.id;
  searchText.value = option.text;
  showDropdown.value = false;
  selectedValue.value = option.id ? option.id.toString() : '';

  if (typeof props.afterSelectChange === 'function') {
    props.afterSelectChange(option);
  }
}

// Update the search text when value changes or on initial load
function updateSearchTextFromValue(val: string | number) {
  if (!val) {
    searchText.value = '';
    return;
  }
  
  // If we have a selected value but no options loaded yet, fetch the option by ID
  if (options.value.length === 0) {
    fetchOptionById(val);
    return;
  }
  
  const selectedOption = options.value.find(option => option.id == val);
  if (selectedOption) {
    searchText.value = selectedOption.text;
  }
}

// Fetch a specific option by ID
async function fetchOptionById(id: string | number) {
  try {
    let itemName = '';
    if(id)
    {
      switch (props.id) {
        case 'licenses': {
          itemName = await getLicenseNameByIdService(id.toString());
          break;
        }
        case 'stations': {
          itemName = await getStationNameByIdService(id.toString());
          break;
        }
        case 'schl': {
          itemName = await getSchlNameByIdService(id.toString());
          break;
        }
      }
    }

    if (itemName) {
      const mappedData = await mapToSelectOption([{
        id: id,
        text: itemName,
      }] as MstMyPageItem[]);

      selectedValue.value = id.toString();
      // Update search text
      const selectedOption = mappedData.find(option => option.id == id);
      if (selectedOption) {
        searchText.value = selectedOption.text;
      }
    }
    else
    {
      searchText.value = '';
    }
  } catch (error) {
    console.error(`Error fetching option by ID ${id}:`, error);
  }
}

async function mapToSelectOption(data: MstMyPageItem[]) {
  return data?.map(p => ({
    id: p.id,
    text: p.text
  })) || [];
}

// Initial load is no longer needed as we'll search on demand
async function loadInitialData() {
  // If we have an initial value, fetch its details
  if (valueField.value.value) {
    await fetchOptionById(valueField.value.value as string);
  }
}

onMounted(async () => {
  await loadInitialData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(ValidatableField, {
      name: __props.name,
      label: __props.label,
      rules: __props.rules,
      type: "hidden",
      modelValue: _unref(valueField).value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(valueField).value) = $event))
    }, null, 8, ["name", "label", "rules", "modelValue"]),
    _withDirectives(_createElementVNode("input", {
      class: "p-recommend-form__input-1 --is-search",
      type: "text",
      autocomplete: "off",
      placeholder: "最低2文字入力し、選択してください",
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchText).value = $event)),
      onInput: handleSearch,
      onFocus: _cache[2] || (_cache[2] = ($event: any) => (showDropdown.value = true)),
      onBlur: handleBlur,
      name: `${__props.name}_text$`
    }, null, 40, _hoisted_2), [
      [_vModelText, searchText.value]
    ]),
    _createElementVNode("ul", {
      class: _normalizeClass(["p-recommend-form__select-list", { '--opened': showDropdown.value && searchText.value.length >= 2 && (_unref(filteredOptions).length > 0 || (searchText.value.length >= 2 && isSearching.value || noResults.value)) }])
    }, [
      (isSearching.value)
        ? (_openBlock(), _createElementBlock("li", _hoisted_3, " 検索中... "))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredOptions), (option) => {
              return (_openBlock(), _createElementBlock("li", {
                key: option.id,
                class: "p-recommend-form__select-list__item",
                onMousedown: _withModifiers(($event: any) => (selectOption(option)), ["prevent"])
              }, _toDisplayString(option.text), 41, _hoisted_4))
            }), 128)),
            (noResults.value)
              ? (_openBlock(), _createElementBlock("li", _hoisted_5, " 該当なし "))
              : _createCommentVNode("", true)
          ], 64))
    ], 2)
  ]))
}
}

})